// Documents module ("Pagine"): a per-workspace page tree with a simple block editor.
// Blocks are plain text lines (paragraph, heading, bulleted/checklist item, quote) — no inline
// rich-text marks (bold/italic/links) yet; this is a real, working editor, just not a full one.
import { z } from 'zod';

const Uuid = z.string().uuid();

export const BLOCK_TYPES = ['paragraph', 'heading1', 'heading2', 'bulleted_item', 'checklist_item', 'quote'] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];
export const BLOCK_TYPE_LABELS: Record<BlockType, string> = {
  paragraph: 'Testo', heading1: 'Titolo grande', heading2: 'Titolo', bulleted_item: 'Elenco puntato',
  checklist_item: 'Checklist', quote: 'Citazione',
};

export const BlockSchema = z.object({
  id: z.string().min(1).max(40),
  type: z.enum(BLOCK_TYPES),
  text: z.string().max(4000),
  checked: z.boolean().optional(),
});
export type Block = z.infer<typeof BlockSchema>;

export const PageContentSchema = z.object({ type: z.literal('doc'), content: z.array(BlockSchema).max(2000) });
export type PageContent = z.infer<typeof PageContentSchema>;

export const emptyPageContent = (): PageContent => ({ type: 'doc', content: [] });

/** Plain text extracted from blocks, used for previews and full-text search. */
export function contentText(content: PageContent): string {
  return content.content.map((b) => b.text).filter(Boolean).join('\n');
}

export const CreatePageInput = z.object({
  workspaceId: Uuid,
  parentPageId: Uuid.nullable().default(null),
  title: z.string().trim().max(300).default(''),
  icon: z.string().trim().max(8).nullable().default(null),
  /** Copies title/icon/content from an existing page (must be a template) instead of starting blank. */
  fromTemplateId: Uuid.optional(),
});
export type CreatePageInput = z.input<typeof CreatePageInput>;

export const UpdatePageInput = z.object({
  title: z.string().trim().max(300),
  icon: z.string().trim().max(8).nullable(),
  content: PageContentSchema,
  isTemplate: z.boolean(),
  visibility: z.enum(['workspace', 'private']),
  parentPageId: Uuid.nullable(),
}).partial();
export type UpdatePageInput = z.input<typeof UpdatePageInput>;

export const RestoreVersionInput = z.object({ versionId: Uuid });

export interface PageSummary {
  id: string;
  workspaceId: string;
  parentPageId: string | null;
  title: string;
  icon: string | null;
  isTemplate: boolean;
  hasChildren: boolean;
  updatedAt: string;
  canEdit: boolean;
}

export interface PageDetail extends PageSummary {
  content: PageContent;
  visibility: 'workspace' | 'private';
  createdBy: { id: string; displayName: string };
  updatedBy: { id: string; displayName: string } | null;
  createdAt: string;
  breadcrumb: { id: string; title: string; icon: string | null }[];
  permissions: { edit: boolean; delete: boolean; share: boolean };
}

export interface PageVersionSummary {
  id: string;
  createdAt: string;
  createdBy: { id: string; displayName: string };
  preview: string;
}
